/** Interview prep curriculum — HLD, LLD, CS fundamentals, behavioral. */

export const ACCENT = '#137274';
export const PINK = '#f50057';

export const slugify = (label) =>
  String(label)
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const t = (label, extra = {}) => ({
  id: slugify(label),
  label,
  status: 'ready',
  ...extra,
});

/**
 * Tracks shown on hub + home teaser.
 * Sections follow industry learning order (foundations → building blocks → problems).
 */
export const INTERVIEW_TRACKS = [
  {
    id: 'system-design',
    title: 'System Design',
    shortTitle: 'HLD',
    eyebrow: 'High-level design',
    summary:
      'Design systems that survive traffic, failure, and growth — the vocabulary FAANG and late-stage startups expect.',
    homeBlurb: 'Scalability, caching, queues, storage, and classic design prompts.',
    accent: '#137274',
    platforms: ['AWS', 'Azure', 'Docker', 'Kubernetes'],
    outcomes: [
      'Clarify requirements & constraints before drawing boxes',
      'Trade CAP, latency, and cost with intent',
      'Defend capacity estimates and failure modes',
    ],
    sections: [
      {
        id: 'foundations',
        title: 'Distributed systems foundations',
        description: 'Core properties every HLD conversation starts from.',
        topics: [
          t('Scalability Basics'),
          t('Back-of-Envelope Estimation'),
          t('CAP Theorem'),
          t('Consistency Models'),
          t('Availability'),
          t('Replication'),
          t('Partitioning'),
          t('Consistent Hashing'),
          t('Quorum & Consensus'),
          t('Leader Election'),
        ],
      },
      {
        id: 'edge-and-traffic',
        title: 'Traffic, edge & delivery',
        description: 'How requests reach services safely and fast.',
        topics: [
          t('DNS & Anycast'),
          t('CDN'),
          t('Load Balancer'),
          t('Reverse Proxy'),
          t('API Gateway'),
          t('Rate Limiting'),
        ],
      },
      {
        id: 'caching',
        title: 'Caching',
        description: 'Latency wins — and the invalidation traps.',
        topics: [
          t('Caching Strategies'),
          t('Cache Aside & Write-Through'),
          t('Redis'),
          t('Memcached'),
          t('Bloom Filters'),
        ],
      },
      {
        id: 'messaging',
        title: 'Messaging & async',
        description: 'Decouple producers, absorb spikes, recover from failure.',
        topics: [
          t('Message Queues'),
          t('Pub/Sub'),
          t('Kafka'),
          t('RabbitMQ'),
          t('Idempotency'),
          t('Retry Strategies'),
          t('Dead Letter Queues'),
        ],
      },
      {
        id: 'storage',
        title: 'Storage & data',
        description: 'Pick the right store — then shard it without regret.',
        topics: [
          t('Storage Overview'),
          t('SQL'),
          t('NoSQL'),
          t('Object Storage'),
          t('Elasticsearch'),
          t('Sharding'),
          t('Secondary Indexes'),
        ],
      },
      {
        id: 'resilience-ops',
        title: 'Resilience & operations',
        description: 'What keeps a design correct and recoverable once it is live in production.',
        topics: [
          t('Distributed Transactions (2PC & Saga)', { id: 'distributed-transactions' }),
          t('Vector Clocks & Conflict Resolution', { id: 'vector-clocks' }),
          t('Gossip Protocol', { id: 'gossip-protocol' }),
          t('Service Discovery', { id: 'service-discovery' }),
          t('Chaos Engineering', { id: 'chaos-engineering' }),
          t('Disaster Recovery (RTO & RPO)', { id: 'disaster-recovery' }),
        ],
      },
      {
        id: 'design-problems',
        title: 'Design problems',
        description: 'Interview staples — practice end-to-end narratives.',
        topics: [
          t('TinyURL / URL Shortener'),
          t('Rate Limiter Service'),
          t('Notification Service'),
          t('News Feed'),
          t('Instagram'),
          t('Twitter / X'),
          t('WhatsApp'),
          t('Dropbox'),
          t('Google Drive'),
          t('YouTube'),
          t('Netflix'),
          t('Ride Sharing'),
          t('Payment Gateway'),
          t('Search Engine'),
          t('Web Crawler'),
          t('Distributed Cache', { id: 'distributed-cache' }),
          t('Typeahead / Autocomplete', { id: 'typeahead-autocomplete' }),
          t('Distributed Task Scheduler', { id: 'distributed-task-scheduler' }),
          t('Distributed ID Generator', { id: 'distributed-id-generator' }),
          t('Proximity Search (Yelp / Google Maps)', { id: 'proximity-search' }),
          t('Ad Click Aggregation', { id: 'ad-click-aggregation' }),
          t('Ticket Booking System', { id: 'ticket-booking-system' }),
          t('Video Conferencing', { id: 'video-conferencing' }),
        ],
      },
    ],
  },
  {
    id: 'lld',
    title: 'Low-Level Design',
    shortTitle: 'LLD',
    eyebrow: 'Object-oriented design',
    summary:
      'Model domains cleanly, apply patterns with judgment, and walk interviewers through maintainable class design.',
    homeBlurb: 'SOLID, patterns, UML, and machine-coding classics.',
    accent: '#2563eb',
    platforms: [],
    outcomes: [
      'Map requirements → classes → interactions',
      'Choose patterns for change, not decoration',
      'Discuss extensibility and test seams',
    ],
    sections: [
      {
        id: 'principles',
        title: 'Principles & modeling',
        description: 'Build the vocabulary before patterns and machine coding.',
        topics: [
          t('OOP Fundamentals'),
          t('Encapsulation & Abstraction'),
          t('Inheritance'),
          t('Composition over Inheritance'),
          t('SOLID'),
          t('UML'),
        ],
      },
      {
        id: 'patterns',
        title: 'Design patterns',
        description: 'Creational → structural → behavioral — know when each earns complexity.',
        topics: [
          t('Factory'),
          t('Builder'),
          t('Singleton'),
          t('Adapter'),
          t('Decorator'),
          t('Facade'),
          t('Proxy'),
          t('Observer'),
          t('Strategy'),
          t('Command'),
          t('State'),
          t('Template Method'),
          t('Chain of Responsibility'),
          t('Visitor'),
          t('Repository'),
        ],
      },
      {
        id: 'design-problems',
        title: 'Design problems',
        description: 'Machine-coding and whiteboard OO designs — simple to complex.',
        topics: [
          t('Tic Tac Toe'),
          t('Coffee Machine'),
          t('Vending Machine'),
          t('Parking Lot'),
          t('Elevator'),
          t('Library Management'),
          t('Snake Game'),
          t('Chess'),
          t('ATM'),
          t('Inventory Management'),
          t('Logging Framework'),
          t('Splitwise'),
          t('BookMyShow'),
        ],
      },
    ],
  },
  {
    id: 'cs-fundamentals',
    title: 'CS Fundamentals & Backend',
    shortTitle: 'CS + Backend',
    eyebrow: 'Foundations that show up in every loop',
    summary:
      'Operating systems, databases, networking, Linux, concurrency, and production backend patterns — the layer under your system design answers.',
    homeBlurb: 'OS, DBMS, networking, concurrency, and production backend.',
    accent: '#d97706',
    platforms: ['Docker', 'Kubernetes'],
    outcomes: [
      'Explain trade-offs with first principles',
      'Debug production issues with OS & network intuition',
      'Design APIs and services with proven patterns',
    ],
    sections: [
      {
        id: 'operating-systems',
        title: 'Operating systems',
        description: 'Process model → scheduling → memory → synchronization.',
        topics: [
          t('Processes'),
          t('Threads'),
          t('Context Switching'),
          t('CPU Scheduling'),
          t('Paging & Virtual Memory'),
          t('Mutex'),
          t('Semaphore'),
          t('Deadlock'),
        ],
      },
      {
        id: 'dbms',
        title: 'DBMS',
        description: 'Schema → reads → transactions → plans.',
        topics: [
          t('Normalization'),
          t('Indexes'),
          t('Joins'),
          t('Transactions & ACID'),
          t('Isolation Levels'),
          t('MVCC'),
          t('Locks'),
          t('Execution Plans'),
          t('Connection Pooling'),
        ],
      },
      {
        id: 'networking',
        title: 'Networking',
        description: 'Name resolution → transport → security → application protocols.',
        topics: [
          t('DNS'),
          t('TCP'),
          t('UDP'),
          t('TLS'),
          t('HTTPS'),
          t('HTTP / HTTP/2 / HTTP/3'),
          t('REST'),
          t('gRPC'),
          t('WebSocket'),
        ],
      },
      {
        id: 'linux',
        title: 'Linux',
        description: 'Day-to-day ops intuition interviewers expect.',
        topics: [
          t('Essential Commands'),
          t('Process Management'),
          t('Memory'),
          t('CPU'),
          t('Disk I/O'),
          t('Networking Tools'),
        ],
      },
      {
        id: 'concurrency',
        title: 'Concurrency',
        description: 'Correctness first, then throughput patterns.',
        topics: [
          t('Race Conditions'),
          t('Synchronization'),
          t('Concurrency Locks', { id: 'concurrency-locks' }),
          t('Thread Pool'),
          t('Executors'),
          t('Async / Await'),
          t('Actors & Message Passing'),
        ],
      },
      {
        id: 'backend',
        title: 'Backend development',
        description: 'Auth → data access → resilience → platforms → service patterns → observability.',
        topics: [
          t('Authentication'),
          t('JWT'),
          t('OAuth'),
          t('RBAC'),
          t('Caching'),
          t('Redis'),
          t('Rate Limiter'),
          t('Circuit Breaker'),
          t('Distributed Lock'),
          t('Kafka'),
          t('RabbitMQ'),
          t('Docker'),
          t('Kubernetes'),
          t('Microservices'),
          t('Saga'),
          t('CQRS'),
          t('Event Sourcing'),
          t('Observability'),
          t('Structured Logging'),
          t('Prometheus'),
          t('Grafana'),
          t('OpenTelemetry'),
        ],
      },
    ],
  },
  {
    id: 'behavioral',
    title: 'Behavioral Interviews',
    shortTitle: 'Behavioral',
    eyebrow: 'Leadership & collaboration signals',
    summary:
      'Structured stories that prove ownership, judgment, and impact — aligned with Amazon LPs, Google guidance, and STAR.',
    homeBlurb: 'STAR stories, leadership principles, and high-signal themes.',
    accent: '#f50057',
    platforms: [],
    outcomes: [
      'Build a bank of ~25 reusable STAR stories',
      'Map stories to leadership principles',
      'Practice crisp delivery under time pressure',
    ],
    sections: [
      {
        id: 'frameworks',
        title: 'Frameworks & company guides',
        description: 'Learn the format before stocking stories.',
        topics: [
          t('STAR Method'),
          t('Building Your Story Bank (~25)'),
          t('Amazon Leadership Principles'),
          t("Google's Behavioral Interview Guidance", {
            id: 'googles-behavioral-interview-guidance',
          }),
        ],
      },
      {
        id: 'themes',
        title: 'High-signal themes',
        description: 'Prepare at least one sharp story per theme.',
        topics: [
          t('Ownership'),
          t('Leadership'),
          t('Conflict'),
          t('Failure & Learning'),
          t('Production Incident'),
          t('Mentoring'),
          t('Promotion & Growth'),
          t('Scaling a System or Team'),
          t('Customer Obsession'),
          t('Bias for Action'),
          t('Dive Deep'),
          t('Disagree and Commit'),
          t('Deliver Results'),
          t('Innovation'),
        ],
      },
    ],
  },
];

export const getTrackById = (trackId) =>
  INTERVIEW_TRACKS.find((track) => track.id === trackId) || null;

export const getTopicInTrack = (trackId, topicId) => {
  const track = getTrackById(trackId);
  if (!track) return null;
  for (const section of track.sections) {
    const topic = section.topics.find((item) => item.id === topicId);
    if (topic) {
      return { track, section, topic };
    }
  }
  return null;
};

export const countTopics = (track) =>
  track.sections.reduce((sum, section) => sum + section.topics.length, 0);
